import React, { useState } from "react";
import { useNewClientWizardContext } from "../Context/NewClientWizardContext";
import styles from "../NewClientWizard.module.css";
import { AccountTypeEntry, AssociatedDtcEntry } from "../types";

const emptyDtc = (): AssociatedDtcEntry => ({
  dtcNumber: "",
  institutionNo: "",
  mpid: "",
  companyName: "",
  agentBank: "",
  bid: "",
  isDefault: false,
});

/**
 * Step 4 — the permission catalogs. Portal user assignment binds permissions to
 * account types and associated DTCs, so a new client needs at least one of each
 * or the permission screen renders empty.
 */
export function CatalogsStep() {
  const { formData, updateFormData } = useNewClientWizardContext();
  const [newAccountType, setNewAccountType] = useState<AccountTypeEntry>({ name: "", abbreviation: "" });
  const [newDtc, setNewDtc] = useState<AssociatedDtcEntry>(emptyDtc());

  const addAccountType = () => {
    if (!newAccountType.name.trim() || !newAccountType.abbreviation.trim()) return;
    updateFormData({ accountTypes: [...formData.accountTypes, { ...newAccountType }] });
    setNewAccountType({ name: "", abbreviation: "" });
  };

  const addDtc = () => {
    if (!newDtc.dtcNumber.trim() || !newDtc.companyName.trim()) return;
    // At most one default, matching the app's Insert_AssociatedDTC behavior; when none
    // is marked, the firm profile acts as the default company.
    const existing = newDtc.isDefault
      ? formData.associatedDtcs.map((d) => ({ ...d, isDefault: false }))
      : [...formData.associatedDtcs];
    updateFormData({ associatedDtcs: [...existing, { ...newDtc }] });
    setNewDtc(emptyDtc());
  };

  return (
    <div className={styles.stepBody}>
      <h4>Account Types (optional)</h4>
      <p className={styles.hint}>
        Users are granted access per account type during user assignment. Seeding them here
        lets users be permissioned immediately; left empty, they can be created in-app later.
      </p>
      <table className={styles.catalogTable}>
        <thead>
          <tr><th>Name</th><th>Abbreviation</th><th /></tr>
        </thead>
        <tbody>
          {formData.accountTypes.map((accountType, index) => (
            <tr key={index}>
              <td>{accountType.name}</td>
              <td>{accountType.abbreviation}</td>
              <td>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() =>
                    updateFormData({
                      accountTypes: formData.accountTypes.filter((_, i) => i !== index),
                    })
                  }
                >
                  remove
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td>
              <input
                type="text"
                value={newAccountType.name}
                placeholder="e.g. Hedge Fund"
                onChange={(e) => setNewAccountType({ ...newAccountType, name: e.target.value })}
              />
            </td>
            <td>
              <input
                type="text"
                value={newAccountType.abbreviation}
                placeholder="e.g. HF"
                onChange={(e) => setNewAccountType({ ...newAccountType, abbreviation: e.target.value })}
              />
            </td>
            <td><button type="button" onClick={addAccountType}>Add</button></td>
          </tr>
        </tbody>
      </table>

      <h4>Associated DTCs (optional)</h4>
      <p className={styles.hint}>
        Additional DTC number(s) users can be permissioned to; left empty, they can be
        created in-app later. Default is optional and exclusive: if no row is marked
        default, the firm profile acts as the default company (matching in-app behavior).
      </p>
      <table className={styles.catalogTable}>
        <thead>
          <tr><th>DTC</th><th>Institution</th><th>MPID</th><th>Company</th><th>Agent Bank</th><th>BID</th><th>Default</th><th /></tr>
        </thead>
        <tbody>
          {formData.associatedDtcs.map((dtc, index) => (
            <tr key={index}>
              <td>{dtc.dtcNumber}</td>
              <td>{dtc.institutionNo}</td>
              <td>{dtc.mpid}</td>
              <td>{dtc.companyName}</td>
              <td>{dtc.agentBank}</td>
              <td>{dtc.bid}</td>
              <td>{dtc.isDefault ? "Yes" : ""}</td>
              <td>
                <button
                  type="button"
                  className={styles.linkButton}
                  onClick={() =>
                    updateFormData({
                      associatedDtcs: formData.associatedDtcs.filter((_, i) => i !== index),
                    })
                  }
                >
                  remove
                </button>
              </td>
            </tr>
          ))}
          <tr>
            <td><input type="text" value={newDtc.dtcNumber} placeholder="0100" onChange={(e) => setNewDtc({ ...newDtc, dtcNumber: e.target.value })} /></td>
            <td><input type="text" value={newDtc.institutionNo} onChange={(e) => setNewDtc({ ...newDtc, institutionNo: e.target.value })} /></td>
            <td><input type="text" value={newDtc.mpid} onChange={(e) => setNewDtc({ ...newDtc, mpid: e.target.value })} /></td>
            <td><input type="text" value={newDtc.companyName} onChange={(e) => setNewDtc({ ...newDtc, companyName: e.target.value })} /></td>
            <td><input type="text" value={newDtc.agentBank} onChange={(e) => setNewDtc({ ...newDtc, agentBank: e.target.value })} /></td>
            <td><input type="text" value={newDtc.bid} onChange={(e) => setNewDtc({ ...newDtc, bid: e.target.value })} /></td>
            <td>
              <input
                type="checkbox"
                checked={newDtc.isDefault}
                onChange={(e) => setNewDtc({ ...newDtc, isDefault: e.target.checked })}
              />
            </td>
            <td><button type="button" onClick={addDtc}>Add</button></td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
